import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Slider,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import { useLazySearchOSMPlacesQuery, useLazySearchGooglePlacesQuery, useImportSelectedPlacesMutation } from '../../../redux/adminApiSlice';
import LeafletMap from '../../../components/Map/LeafletMap';

const DEFAULT_CENTER = { lat: 40.7580, lng: -73.9855 }; // Times Square, NYC
const DEFAULT_RADIUS_MILES = 1; // 1 mile
const MIN_RADIUS_MILES = 0.1; // 0.1 miles (~160m)
const MAX_RADIUS_MILES = 10; // 10 miles

// Convert miles to meters (1 mile = 1609.34 meters)
const milesToMeters = (miles: number): number => Math.round(miles * 1609.34);

// OSM Place Types (amenity/leisure/shop tags)
const OSM_PLACE_TYPES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'fast_food', label: 'Fast Food' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'bar', label: 'Bar' },
  { value: 'pub', label: 'Pub' },
  { value: 'nightclub', label: 'Nightclub' },
  { value: 'cinema', label: 'Cinema' },
  { value: 'theatre', label: 'Theatre' },
  { value: 'museum', label: 'Museum' },
  { value: 'art_gallery', label: 'Art Gallery' },
  { value: 'supermarket', label: 'Supermarket' },
  { value: 'convenience', label: 'Convenience Store' },
  { value: 'clothes', label: 'Clothing Store' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'gym', label: 'Gym' },
  { value: 'park', label: 'Park' },
  { value: 'library', label: 'Library' },
  { value: 'school', label: 'School' },
  { value: 'university', label: 'University' },
];

interface OSMPlace {
  id: string;
  name: string;
  type: string;
  coordinates: { lat: number; lng: number };
  address?: string;
  tags?: Record<string, string>;
  isDuplicate?: boolean; // Marked as duplicate if place already exists in database
}

const GooglePlacesImport: React.FC = () => {
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number }>(DEFAULT_CENTER);
  const [radiusMiles, setRadiusMiles] = useState<number>(DEFAULT_RADIUS_MILES);
  const [locationSearch, setLocationSearch] = useState<string>('');
  const [isSearchingLocation, setIsSearchingLocation] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedPlaceTypes, setSelectedPlaceTypes] = useState<string[]>([]);
  const [useGooglePlacesDirect, setUseGooglePlacesDirect] = useState<boolean>(false);
  const [osmPlaces, setOsmPlaces] = useState<OSMPlace[]>([]);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<Set<string>>(new Set());
  const [importResult, setImportResult] = useState<any>(null);
  const [placeNameSearch, setPlaceNameSearch] = useState<string>('');
  const [importByNameResult, setImportByNameResult] = useState<any>(null);

  const [searchOSM, { isLoading: isSearchingOSM }] = useLazySearchOSMPlacesQuery();
  const [searchGooglePlaces, { isLoading: isSearchingGoogle }] = useLazySearchGooglePlacesQuery();
  const [importSelected, { isLoading: isImporting }] = useImportSelectedPlacesMutation();
  
  const isSearching = isSearchingOSM || isSearchingGoogle;
  const searchAbortRef = useRef<AbortController | null>(null);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapHeight, setMapHeight] = useState(500);

  useEffect(() => {
    const calculateMapHeight = () => {
      if (mapContainerRef.current) {
        const rect = mapContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const newHeight = viewportHeight - rect.top - 24;
        setMapHeight(Math.max(300, newHeight));
      }
    };

    calculateMapHeight();
    window.addEventListener('resize', calculateMapHeight);
    return () => window.removeEventListener('resize', calculateMapHeight);
  }, []);

  const handleMarkerDrag = useCallback((position: { lat: number; lng: number }) => {
    setSelectedLocation(position);
    setOsmPlaces([]); // Clear results when location changes
    setSelectedPlaceIds(new Set());
    setImportResult(null);
  }, []);

  const handleLocationSearch = useCallback(async () => {
    if (!locationSearch.trim()) {
      setSearchError('Please enter a city and state');
      return;
    }

    setIsSearchingLocation(true);
    setSearchError(null);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationSearch)}&limit=1`,
        {
          headers: {
            'User-Agent': 'Grid Admin Console',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Geocoding request failed');
      }

      const data = await response.json();

      if (data.length === 0) {
        setSearchError('Location not found. Please try a different search term.');
        return;
      }

      const result = data[0];
      const newLocation = {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
      };

      setSelectedLocation(newLocation);
      setLocationSearch('');
      setOsmPlaces([]);
      setSelectedPlaceIds(new Set());
      setImportResult(null);
    } catch (error: any) {
      setSearchError(error.message || 'Failed to search location');
    } finally {
      setIsSearchingLocation(false);
    }
  }, [locationSearch]);

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLocationSearch();
    }
  };

  const handleCancelSearch = () => {
    // Abort the current search request
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
    // Clear state immediately
    setOsmPlaces([]);
    setSelectedPlaceIds(new Set());
    setSearchError(null);
  };

  const handleSearchPlaces = async () => {
    // Create abort controller for this search
    const abortController = new AbortController();
    searchAbortRef.current = abortController;

    try {
      setOsmPlaces([]);
      setSelectedPlaceIds(new Set());
      setImportResult(null);
      setSearchError(null);

      let result;
      if (useGooglePlacesDirect) {
        // Search Google Places API directly
        const queryPromise = searchGooglePlaces({
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
          radiusMeters: milesToMeters(radiusMiles),
          includedTypes: selectedPlaceTypes.length > 0 ? selectedPlaceTypes : undefined,
        });

        // Check if cancelled before waiting for result
        if (abortController.signal.aborted) {
          return;
        }

        result = await queryPromise.unwrap();

        // Check if cancelled after getting result
        if (abortController.signal.aborted) {
          return;
        }
      } else {
        // Search OSM (free)
        const queryPromise = searchOSM({
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
          radiusMeters: milesToMeters(radiusMiles),
          placeTypes: selectedPlaceTypes.length > 0 ? selectedPlaceTypes : undefined,
        });

        // Check if cancelled before waiting for result
        if (abortController.signal.aborted) {
          return;
        }

        result = await queryPromise.unwrap();

        // Check if cancelled after getting result
        if (abortController.signal.aborted) {
          return;
        }
      }

      // Only set results if not cancelled
      if (!abortController.signal.aborted) {
        setOsmPlaces(result.places || []);
      }
    } catch (error: any) {
      // Don't show error if search was cancelled
      if (abortController.signal.aborted || error?.name === 'AbortError') {
        return;
      }
      setSearchError(error?.data?.message || error?.message || `Failed to search ${useGooglePlacesDirect ? 'Google Places' : 'OSM'}`);
    } finally {
      // Clear abort ref if this was the active search
      if (searchAbortRef.current === abortController) {
        searchAbortRef.current = null;
      }
    }
  };

  const handleTogglePlace = (placeId: string) => {
    // Don't allow selecting duplicates
    const place = osmPlaces.find(p => p.id === placeId);
    if (place?.isDuplicate) {
      return;
    }
    
    const newSelected = new Set(selectedPlaceIds);
    if (newSelected.has(placeId)) {
      newSelected.delete(placeId);
    } else {
      newSelected.add(placeId);
    }
    setSelectedPlaceIds(newSelected);
  };

  const handleSelectAll = () => {
    // Only select non-duplicate places
    const nonDuplicatePlaces = osmPlaces.filter(p => !p.isDuplicate);
    
    if (selectedPlaceIds.size === nonDuplicatePlaces.length) {
      setSelectedPlaceIds(new Set());
    } else {
      setSelectedPlaceIds(new Set(nonDuplicatePlaces.map(p => p.id)));
    }
  };

  const handleImportByName = async () => {
    const name = placeNameSearch.trim();
    if (!name) {
      setImportByNameResult({ success: false, errors: ['Please enter a place name'] });
      return;
    }

    try {
      setImportByNameResult(null);
      const result = await importSelected({
        places: [{
          name,
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
        }],
      }).unwrap();
      setImportByNameResult(result);
      if (result.success && result.stats?.totalPlacesImported) {
        setPlaceNameSearch('');
      }
    } catch (error: any) {
      setImportByNameResult({
        success: false,
        errors: [error?.data?.message || error?.message || 'Import failed'],
        stats: { totalPlacesImported: 0, totalPlacesSkipped: 0, totalErrors: 1 },
      });
    }
  };

  const handleImportSelected = async () => {
    if (selectedPlaceIds.size === 0) {
      setSearchError('Please select at least one place to import');
      return;
    }

    try {
      setImportResult(null);
      // Filter out duplicates (shouldn't happen if UI is working correctly, but double-check)
      const placesToImport = osmPlaces
        .filter(p => selectedPlaceIds.has(p.id) && !p.isDuplicate)
        .map(p => ({
          name: p.name,
          latitude: p.coordinates.lat,
          longitude: p.coordinates.lng,
        }));

      const result = await importSelected({
        places: placesToImport,
      }).unwrap();

      setImportResult(result);
    } catch (error: any) {
      setImportResult({
        success: false,
        errors: [error?.data?.message || error?.message || 'Import failed'],
        stats: {
          totalPlacesFound: 0,
          totalPlacesImported: 0,
          totalPlacesSkipped: 0,
          totalErrors: 1,
          costBreakdown: {
            nearbySearchCost: 0,
            placeDetailsCost: 0,
            photoCost: 0,
            totalCost: 0,
          },
        },
      });
    }
  };

  return (
    <Box sx={{ 
      p: 3, 
      height: '100%',
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      <Typography variant="h4" gutterBottom>
        Import Google Places
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Step 1: Search for places using free OSM data. Step 2: Select places to import. Step 3: Import selected places using Google Places API (costs apply).
      </Typography>

      {/* Add place by name */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Add Place by Name
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search Google Places by name. Uses the map center for location bias—drag the marker to refine the search area.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            label="Place name"
            placeholder="e.g., Wayan, The Slipper Room"
            value={placeNameSearch}
            onChange={(e) => {
              setPlaceNameSearch(e.target.value);
              setImportByNameResult(null);
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleImportByName()}
            sx={{ minWidth: 280 }}
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleImportByName}
            disabled={isImporting || !placeNameSearch.trim()}
          >
            {isImporting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Import by Name
          </Button>
        </Box>
        {importByNameResult && (
          <Box sx={{ mt: 2 }}>
            {importByNameResult.success ? (
              <Alert severity="success">
                Successfully imported {importByNameResult.stats?.totalPlacesImported} place(s).
                {importByNameResult.results?.[0]?.googlePlaceId && ' Google Place ID: ' + importByNameResult.results[0].googlePlaceId}
              </Alert>
            ) : (
              <Alert severity="error">
                {importByNameResult.errors?.join(' ') || importByNameResult.results?.[0]?.reason || 'Import failed'}
              </Alert>
            )}
            {importByNameResult.stats?.costBreakdown?.totalCost != null && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Cost: ${importByNameResult.stats.costBreakdown.totalCost.toFixed(3)}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      <Grid container spacing={3} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Map Section */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Paper sx={{ p: 2, mb: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Typography variant="h6" gutterBottom>
              Select Location
            </Typography>
            
            {/* Location Search Input and Search Button */}
            <Box sx={{ mb: 2, flexShrink: 0, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Search City and State"
                placeholder="e.g., New York, NY or Boston, MA"
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                disabled={isSearchingLocation}
                error={!!searchError}
                helperText={searchError || 'Enter a city and state to center the map'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleLocationSearch}
                        disabled={isSearchingLocation || !locationSearch.trim()}
                        edge="end"
                      >
                        {isSearchingLocation ? (
                          <CircularProgress size={20} />
                        ) : (
                          <SearchIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1, width: '100%' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearchPlaces}
                  disabled={isSearching}
                  sx={{ whiteSpace: 'nowrap', flex: 1 }}
                >
                  {isSearching ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Searching...
                    </>
                  ) : (
                    'Find Places'
                  )}
                </Button>
                {isSearching && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancelSearch}
                    sx={{ whiteSpace: 'nowrap' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Box>

            {/* Search Method Toggle */}
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useGooglePlacesDirect}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUseGooglePlacesDirect(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      Use Google Places API directly
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {useGooglePlacesDirect 
                        ? 'Searches Google Places API (costs money)'
                        : 'Searches OpenStreetMap first (free)'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            {/* Place Types Selector */}
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <FormControl fullWidth>
                <InputLabel id="place-types-label">Place Types (Optional)</InputLabel>
                <Select
                  labelId="place-types-label"
                  multiple
                  value={selectedPlaceTypes}
                  onChange={(e) => {
                    setSelectedPlaceTypes(e.target.value as string[]);
                    setOsmPlaces([]);
                    setSelectedPlaceIds(new Set());
                    setImportResult(null);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip 
                          key={value} 
                          label={OSM_PLACE_TYPES.find(type => type.value === value)?.label || value} 
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {OSM_PLACE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Filter results by place type. Leave empty to show all types.
                </Typography>
              </FormControl>
            </Box>

            {/* Radius Slider */}
            <Box sx={{ mb: 2, flexShrink: 0 }}>
              <Typography gutterBottom>
                Search Radius: {radiusMiles.toFixed(1)} {radiusMiles === 1 ? 'mile' : 'miles'}
              </Typography>
              <Slider
                value={radiusMiles}
                onChange={(_, value) => {
                  setRadiusMiles(value as number);
                  setOsmPlaces([]);
                  setSelectedPlaceIds(new Set());
                  setImportResult(null);
                }}
                min={MIN_RADIUS_MILES}
                max={MAX_RADIUS_MILES}
                step={0.1}
                marks={[
                  { value: 0.5, label: '0.5 mi' },
                  { value: 1, label: '1 mi' },
                  { value: 2, label: '2 mi' },
                  { value: 5, label: '5 mi' },
                  { value: 10, label: '10 mi' },
                ]}
              />
            </Box>

            <Box ref={mapContainerRef} sx={{ position: 'relative', flex: 1, borderRadius: 1, overflow: 'hidden', minHeight: 0 }}>
              <LeafletMap
                center={selectedLocation}
                zoom={15}
                height={mapHeight}
                draggable={true}
                onLocationChange={handleMarkerDrag}
                searchRadiusMeters={milesToMeters(radiusMiles)}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  bgcolor: 'background.paper',
                  p: 1,
                  borderRadius: 1,
                  boxShadow: 2,
                  zIndex: 1000,
                }}
              >
                <Typography variant="caption" display="block">
                  Lat: {selectedLocation.lat.toFixed(6)}
                </Typography>
                <Typography variant="caption" display="block">
                  Lng: {selectedLocation.lng.toFixed(6)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Results Section */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%' }}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, maxHeight: '100%', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
              <Box>
                <Typography variant="h6">
                  Places Found ({osmPlaces.length})
                </Typography>
                {osmPlaces.some(p => p.isDuplicate) && (
                  <Typography variant="caption" color="text.secondary">
                    {osmPlaces.filter(p => p.isDuplicate).length} duplicate(s) already in database
                  </Typography>
                )}
              </Box>
              {osmPlaces.length > 0 && (
                <Box>
                  <Button size="small" onClick={handleSelectAll}>
                    {selectedPlaceIds.size === osmPlaces.filter(p => !p.isDuplicate).length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Chip
                    label={`${selectedPlaceIds.size} selected`}
                    color="primary"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Box>
              )}
            </Box>

            {osmPlaces.length === 0 && !isSearchingOSM && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Click "Find Places" to search for places in the selected area
                </Typography>
              </Box>
            )}

            {isSearchingOSM && (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress />
              </Box>
            )}

            {osmPlaces.length > 0 && (
              <>
                <Box 
                  sx={{ 
                    flex: 1, 
                    minHeight: 0,
                    maxHeight: '100%',
                    display: 'flex', 
                    flexDirection: 'column', 
                    overflow: 'hidden',
                    mb: 2
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                      },
                    }}
                  >
                    <List sx={{ p: 0 }}>
                      {osmPlaces.map((place) => {
                        const isDuplicate = place.isDuplicate || false;
                        const isSelected = selectedPlaceIds.has(place.id);
                        
                        return (
                          <ListItem key={place.id} disablePadding>
                            <ListItemButton 
                              onClick={() => handleTogglePlace(place.id)}
                              disabled={isDuplicate}
                              sx={{
                                opacity: isDuplicate ? 0.5 : 1,
                                cursor: isDuplicate ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  disabled={isDuplicate}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography>{place.name}</Typography>
                                    {isDuplicate && (
                                      <Chip 
                                        label="Duplicate" 
                                        size="small" 
                                        color="warning"
                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                      />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <>
                                    <Typography component="span" variant="body2" color="text.secondary">
                                      {place.type}
                                      {place.address && ` • ${place.address}`}
                                    </Typography>
                                  </>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2, flexShrink: 0 }} />

                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  onClick={handleImportSelected}
                  disabled={isImporting || selectedPlaceIds.size === 0}
                  size="large"
                  sx={{ flexShrink: 0 }}
                >
                  {isImporting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Importing Selected Places...
                    </>
                  ) : (
                    `Import ${selectedPlaceIds.size} Selected Place${selectedPlaceIds.size !== 1 ? 's' : ''} (Google Places API)`
                  )}
                </Button>
              </>
            )}
          </Paper>
        </Grid>

        {/* Import Results */}
        {importResult && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Import Results
                </Typography>

                {importResult.success ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Import completed successfully!
                  </Alert>
                ) : (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Import completed with errors
                  </Alert>
                )}

                {importResult.stats && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Statistics:
                    </Typography>
                    <Typography variant="body2">
                      • Places Found: {importResult.stats.totalPlacesFound}
                    </Typography>
                    <Typography variant="body2">
                      • Places Imported: {importResult.stats.totalPlacesImported}
                    </Typography>
                    <Typography variant="body2">
                      • Places Skipped (duplicates/no location/no type): {importResult.stats.totalPlacesSkipped}
                    </Typography>
                    <Typography variant="body2">
                      • Errors: {importResult.stats.totalErrors}
                    </Typography>
                  </Box>
                )}

                {importResult.stats?.costBreakdown && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Cost Breakdown:
                    </Typography>
                    <Typography variant="body2">
                      • Text Search: ${importResult.stats.costBreakdown.nearbySearchCost.toFixed(3)}
                    </Typography>
                    <Typography variant="body2">
                      • Place Details: ${importResult.stats.costBreakdown.placeDetailsCost.toFixed(3)}
                    </Typography>
                    <Typography variant="body2">
                      • Photos: ${importResult.stats.costBreakdown.photoCost.toFixed(3)}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      • Total Cost: ${importResult.stats.costBreakdown.totalCost.toFixed(3)}
                    </Typography>
                  </Box>
                )}

                {importResult.errors && importResult.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Errors:
                    </Typography>
                    {importResult.errors.map((error: string, index: number) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        {error}
                      </Alert>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default GooglePlacesImport;
